import { useEffect, useState } from 'react';

/**
 * Seções da página única, na ordem em que aparecem.
 *
 * A numeração faz parte do desenho do protótipo: ela reaparece no kicker de
 * cada seção, e é o que amarra a navegação ao conteúdo.
 */
const SECOES = [
  { id: 'atelie', numero: '01', rotulo: 'Ateliê' },
  { id: 'ambientes', numero: '02', rotulo: 'Ambientes' },
  { id: 'projetos', numero: '03', rotulo: 'Projetos' },
  { id: 'processo', numero: '04', rotulo: 'Processo' },
  { id: 'showroom', numero: '05', rotulo: 'Showroom' },
] as const;

/** Marca da Plane, desenhada no protótipo. */
function Marca() {
  return (
    <svg viewBox="0 0 40 40" aria-hidden="true" className="h-9 w-9 shrink-0">
      <rect
        x="1"
        y="1"
        width="38"
        height="38"
        rx="9"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      <path
        d="M12 29V11h8.6c3.7 0 6 2.3 6 5.7s-2.3 5.7-6 5.7H16V29z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <line x1="12" y1="33.2" x2="28" y2="33.2" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

/**
 * Barra de progresso da leitura, sob o cabeçalho.
 *
 * Vem do protótipo. Fica em `requestAnimationFrame` para não recalcular a cada
 * evento de rolagem, e some para quem pediu menos movimento.
 */
function useProgressoDeLeitura(): number {
  const [progresso, setProgresso] = useState(0);

  useEffect(() => {
    let pendente = false;

    function medir() {
      const total = document.documentElement.scrollHeight - window.innerHeight;

      setProgresso(total > 0 ? Math.min(1, window.scrollY / total) : 0);
      pendente = false;
    }

    function aoRolar() {
      if (pendente) return;

      pendente = true;
      requestAnimationFrame(medir);
    }

    medir();
    window.addEventListener('scroll', aoRolar, { passive: true });
    window.addEventListener('resize', aoRolar);

    return () => {
      window.removeEventListener('scroll', aoRolar);
      window.removeEventListener('resize', aoRolar);
    };
  }, []);

  return progresso;
}

export function SiteHeader() {
  const progresso = useProgressoDeLeitura();
  const [menuAberto, setMenuAberto] = useState(false);

  // Fecha o menu ao navegar para uma âncora, senão ele cobriria o destino.
  const fechar = () => setMenuAberto(false);

  return (
    <header className="sticky top-0 z-50 bg-brand-950/95 text-bone backdrop-blur">
      <div className="mx-auto flex max-w-[1360px] items-center justify-between gap-6 px-6 py-4">
        <a href="#topo" aria-label="Plane Móveis — início" className="flex items-center gap-3">
          <Marca />
          <span className="flex flex-col leading-tight">
            <strong className="font-display text-base tracking-[0.14em]">PLANE</strong>
            <em className="text-[0.6rem] tracking-[0.16em] text-brand-150 not-italic uppercase">
              marcenaria &amp; planejados
            </em>
          </span>
        </a>

        <nav
          id="menu-principal"
          aria-label="Navegação principal"
          className={`${
            menuAberto ? 'flex' : 'hidden'
          } absolute inset-x-0 top-full flex-col gap-1 bg-brand-950 px-6 pb-6 lg:static lg:flex lg:flex-row lg:items-center lg:gap-7 lg:bg-transparent lg:px-0 lg:pb-0`}
        >
          {SECOES.map((secao) => (
            <a
              key={secao.id}
              href={`#${secao.id}`}
              onClick={fechar}
              className="flex items-baseline gap-2 py-2 text-sm text-brand-150 transition-colors hover:text-bone lg:py-0"
            >
              <span aria-hidden="true" className="text-[0.6rem] tracking-[0.14em] text-brass">
                {secao.numero}
              </span>
              {secao.rotulo}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="#orcamento"
            className="hidden bg-brass px-5 py-3 text-[0.72rem] tracking-[0.14em] text-brand-950 uppercase transition-colors hover:bg-bone sm:inline-block"
          >
            Solicitar orçamento
          </a>

          <button
            type="button"
            aria-expanded={menuAberto}
            aria-controls="menu-principal"
            aria-label={menuAberto ? 'Fechar menu' : 'Abrir menu'}
            onClick={() => setMenuAberto((aberto) => !aberto)}
            className="flex h-10 w-10 flex-col items-center justify-center gap-1.5 lg:hidden"
          >
            <span aria-hidden="true" className="h-px w-5 bg-bone" />
            <span aria-hidden="true" className="h-px w-5 bg-bone" />
          </button>
        </div>
      </div>

      <div className="h-px bg-bone/15">
        <span
          aria-hidden="true"
          className="block h-full bg-brass motion-reduce:hidden"
          style={{ width: `${progresso * 100}%` }}
        />
      </div>
    </header>
  );
}
